import React from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Archive, RotateCcw, Edit3, BookText } from "lucide-react";

const CATEGORIES = ["general", "confidentiality", "term", "sla", "payment", "ip", "liability", "termination", "governing_law"];
const TYPES = ["nda", "sla", "msa"];

function ClauseDialog({ open, onClose, clause, onSaved }) {
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const editing = !!clause;
  React.useEffect(() => {
    if (!open) return;
    setForm(clause ? { ...clause } : { title: "", category: "general", body: "", required: false, applicable_types: [...TYPES], partner_types: [], display_order: 999 });
  }, [open]);
  const toggleType = (t) => setForm((f) => ({ ...f, applicable_types: (f.applicable_types || []).includes(t) ? f.applicable_types.filter((x) => x !== t) : [...(f.applicable_types || []), t] }));
  const save = async () => {
    if (!form.title?.trim() || !form.body?.trim()) return toast.error("Title and body are required");
    setBusy(true);
    try {
      if (editing) await api.patch(`/admin/agreements/clauses/${clause.key}`, { title: form.title, category: form.category, body: form.body, required: form.required, applicable_types: form.applicable_types, partner_types: form.partner_types, display_order: Number(form.display_order) });
      else await api.post("/admin/agreements/clauses", { title: form.title, category: form.category, body: form.body, required: form.required, applicable_types: form.applicable_types, partner_types: form.partner_types, display_order: Number(form.display_order) });
      toast.success(editing ? "Clause updated" : "Clause created"); onClose(); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit Clause" : "New Clause"}</DialogTitle>
          <DialogDescription>Use {"{{party_a.legal_name}}"}, {"{{party_b.short_name}}"}, {"{{agreement.effective_date}}"} as merge fields.</DialogDescription></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="clause-title" /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="clause-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Clause Body</Label><Textarea value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={7} className="mt-1 font-mono text-xs" data-testid="clause-body" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Applicable Agreement Types</Label>
              <div className="flex gap-2 mt-2">{TYPES.map((t) => (
                <button key={t} type="button" onClick={() => toggleType(t)} data-testid={`clause-type-${t}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold border ${(form.applicable_types || []).includes(t) ? "bg-vs-royal text-white border-vs-royal" : "bg-white text-slate-500 border-slate-200"}`}>{t.toUpperCase()}</button>
              ))}</div></div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} data-testid="clause-required" />Required clause</label>
              <div><Label className="text-xs">Order</Label><Input type="number" value={form.display_order ?? 999} onChange={(e) => setForm({ ...form, display_order: e.target.value })} className="mt-1 w-20" data-testid="clause-order" /></div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4"><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-vs-royal text-white" data-testid="clause-save">{editing ? "Save" : "Create"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClauseLibrary() {
  const [items, setItems] = React.useState(null);
  const [dialog, setDialog] = React.useState({ open: false, clause: null });

  const load = React.useCallback(() => {
    api.get("/admin/agreements/clauses").then((r) => setItems(r.data.items || [])).catch((e) => { setItems([]); toast.error(apiError(e.response?.data?.detail)); });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const archive = (c) => api.delete(`/admin/agreements/clauses/${c.key}`).then(() => { toast.success("Clause archived"); load(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const restore = (c) => api.post(`/admin/agreements/clauses/${c.key}/restore`).then(() => { toast.success("Clause restored"); load(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));

  return (
    <div>
      <PageHeader title="Clause Library" description="Reusable, versioned legal clauses that compose NDAs, SLAs and MSAs." breadcrumb="VS Command Center · Agreements · Clause Library">
        <Button className="bg-vs-royal text-white" onClick={() => setDialog({ open: true, clause: null })} data-testid="new-clause-btn"><Plus className="h-4 w-4 mr-1" />New Clause</Button>
      </PageHeader>

      {items === null ? <SkeletonRows rows={6} /> : items.length === 0 ? (
        <EmptyState icon="BookText" title="No clauses" />
      ) : (
        <div className="space-y-3" data-testid="clause-list">
          {items.map((c) => (
            <Card key={c.key} className={`!p-4 ${!c.active ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="h-7 w-7 rounded-lg bg-vs-light flex items-center justify-center shrink-0"><BookText className="h-3.5 w-3.5 text-vs-royal" /></div>
                    <span className="font-semibold text-slate-800">{c.title}</span>
                    <span className="text-[11px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5 capitalize">{(c.category || "").replace(/_/g, " ")}</span>
                    {c.required && <span className="text-[11px] rounded-full bg-rose-50 text-rose-600 px-2 py-0.5">Required</span>}
                    <span className="text-[11px] text-slate-400">v{c.version}</span>
                    {(c.applicable_types || []).map((t) => <span key={t} className="text-[10px] font-bold text-vs-royal">{t.toUpperCase()}</span>)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{c.body}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, clause: c })} data-testid={`clause-edit-${c.key}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                  {c.active ? <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" onClick={() => archive(c)} data-testid={`clause-archive-${c.key}`}><Archive className="h-3.5 w-3.5" /></Button>
                    : <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200" onClick={() => restore(c)} data-testid={`clause-restore-${c.key}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <ClauseDialog open={dialog.open} onClose={() => setDialog({ open: false, clause: null })} clause={dialog.clause} onSaved={load} />
    </div>
  );
}
